import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Star, Send, CheckCircle2, Pencil, Trash2, Loader2, Quote, Sparkles, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";

const API = `${window.location.origin}/api`;

const StarIcon = ({ filled, half = false, className = "w-5 h-5" }) => {
  if (half) {
    return (
      <span className={`relative inline-flex ${className}`}>
        <Star className={`${className} text-[#E5E7EB]`} fill="currentColor" />
        <span className="absolute inset-y-0 left-0 overflow-hidden" style={{ width: "50%" }}>
          <Star className={`${className} text-[#FBBF24]`} fill="currentColor" />
        </span>
      </span>
    );
  }
  return (
    <Star
      className={`${className} ${filled ? "text-[#FBBF24]" : "text-[#E5E7EB]"}`}
      fill="currentColor"
    />
  );
};

const StarsDisplay = ({ value, size = "w-5 h-5" }) => {
  // Render 5 stars, supports halves
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => {
        const diff = value - (i - 1);
        if (diff >= 1) return <StarIcon key={i} filled className={size} />;
        if (diff >= 0.5) return <StarIcon key={i} half className={size} />;
        return <StarIcon key={i} filled={false} className={size} />;
      })}
    </span>
  );
};

const StarPicker = ({ value, onChange, disabled = false }) => {
  const [hover, setHover] = useState(0);
  const display = hover || value;
  return (
    <div className="inline-flex items-center gap-1" dir="ltr">
      {[1, 2, 3, 4, 5].map((i) => (
        <button
          key={i}
          type="button"
          disabled={disabled}
          onMouseEnter={() => setHover(i)}
          onMouseLeave={() => setHover(0)}
          onClick={() => onChange(i)}
          className={`p-1 transition-transform ${disabled ? "" : "hover:scale-125"}`}
          aria-label={`${i} نجوم`}
          data-testid={`star-pick-${i}`}
        >
          <Star
            className={`w-8 h-8 ${i <= display ? "text-[#FBBF24]" : "text-[#E5E7EB]"}`}
            fill="currentColor"
          />
        </button>
      ))}
    </div>
  );
};

const formatRelative = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const day = 86400000;
    if (diffMs < day) return "اليوم";
    if (diffMs < 2 * day) return "أمس";
    const days = Math.floor(diffMs / day);
    if (days < 30) return `قبل ${days} يوم`;
    const months = Math.floor(days / 30);
    if (months < 12) return `قبل ${months} شهر`;
    const years = Math.floor(months / 12);
    return `قبل ${years} سنة`;
  } catch (e) {
    return "";
  }
};

const ReviewCard = ({ review }) => (
  <article
    className="relative bg-white rounded-2xl p-6 border border-[#E2E8F0] shadow-md hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col h-full"
    data-testid={`review-card-${review.review_id}`}
  >
    <Quote className="absolute top-4 left-4 w-8 h-8 text-[#7C3AED]/10" />
    <div className="flex items-start gap-3 mb-3">
      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#EC4899] flex items-center justify-center text-white font-black text-lg shadow-md flex-shrink-0 overflow-hidden">
        {review.user_picture ? (
          <img src={review.user_picture} alt={review.user_name} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
        ) : (
          <span>{(review.user_name || "م").charAt(0)}</span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <p className="font-bold text-[#1A1D3D] text-sm md:text-base truncate">{review.user_name}</p>
          {review.verified_purchase && (
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-bold border border-emerald-200">
              <CheckCircle2 className="w-3 h-3" />
              مشترك مُتحقَّق
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 mt-1">
          <StarsDisplay value={review.rating} size="w-4 h-4" />
          <span className="text-xs text-[#8B92B3]">{formatRelative(review.created_at)}</span>
        </div>
      </div>
    </div>
    {review.comment ? (
      <p className="text-sm md:text-base text-[#4A5078] leading-relaxed whitespace-pre-line flex-1">
        {review.comment}
      </p>
    ) : (
      <p className="text-sm text-[#8B92B3] italic flex-1">— لم يُضِف تعليقًا —</p>
    )}
  </article>
);

const ReviewForm = ({ existing, onSubmitted, onCancel }) => {
  const [rating, setRating] = useState(existing?.rating || 0);
  const [comment, setComment] = useState(existing?.comment || "");
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const submit = async () => {
    if (rating < 1) {
      toast.error("يرجى اختيار تقييم من 1 إلى 5 نجوم");
      return;
    }
    if (comment.length > 1000) {
      toast.error("التعليق طويل جدًا (الحد الأقصى 1000 حرف)");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/reviews`,
        { rating, comment: comment.trim() || null },
        { withCredentials: true }
      );
      toast.success(res.data?.updated ? "تم تحديث تقييمك" : "شكرًا! تم نشر تقييمك");
      onSubmitted?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail === "enrollment_required") {
        toast.error("التقييم متاح فقط للمشاركين المسجَّلين في الكورس");
      } else {
        toast.error("تعذّر إرسال التقييم");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("هل تريد حذف تقييمك؟")) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/reviews/me`, { withCredentials: true });
      toast.success("تم حذف تقييمك");
      onSubmitted?.();
    } catch (err) {
      toast.error("تعذّر حذف التقييم");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-[#E2E8F0] shadow-lg p-6 md:p-8" data-testid="review-form">
      <h3 className="text-lg md:text-xl font-black text-[#1A1D3D] mb-1">
        {existing ? "تعديل تقييمك" : "شاركنا تجربتك مع الكورس"}
      </h3>
      <p className="text-sm text-[#4A5078] mb-5">رأيك يُساعد الآخرين على اتخاذ القرار.</p>

      <div className="mb-5">
        <label className="block text-sm font-bold text-[#1A1D3D] mb-2">التقييم</label>
        <StarPicker value={rating} onChange={setRating} disabled={submitting} />
      </div>

      <div className="mb-5">
        <label className="block text-sm font-bold text-[#1A1D3D] mb-2">تعليقك (اختياري)</label>
        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          maxLength={1000}
          rows={5}
          placeholder="اكتب رأيك عن الكورس..."
          className="w-full p-3 rounded-xl border border-[#E2E8F0] focus:border-[#7C3AED] focus:ring-4 focus:ring-purple-100 outline-none text-sm md:text-base resize-y text-[#1A1D3D]"
          data-testid="review-comment-input"
          disabled={submitting}
        />
        <p className="text-xs text-[#8B92B3] mt-1" dir="ltr">{comment.length} / 1000</p>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Button
            onClick={submit}
            disabled={submitting || rating < 1}
            className="bg-gradient-to-r from-[#7C3AED] to-[#EC4899] hover:from-[#6D28D9] hover:to-[#DB2777] text-white px-6"
            data-testid="review-submit-button"
          >
            {submitting ? <Loader2 className="w-4 h-4 ml-2 animate-spin" /> : <Send className="w-4 h-4 ml-2" />}
            {existing ? "تحديث التقييم" : "نشر التقييم"}
          </Button>
          {onCancel && (
            <Button variant="outline" onClick={onCancel} disabled={submitting}>
              إلغاء
            </Button>
          )}
        </div>
        {existing && (
          <Button
            variant="ghost"
            onClick={handleDelete}
            disabled={deleting}
            className="text-[#DC2626] hover:text-[#B91C1C] hover:bg-red-50"
            data-testid="review-delete-button"
          >
            <Trash2 className="w-4 h-4 ml-1" />
            حذف تقييمي
          </Button>
        )}
      </div>
    </div>
  );
};

const RatingSummary = ({ stats }) => {
  const total = stats?.total || 0;
  const avg = stats?.average || 0;
  const dist = stats?.distribution || { "1": 0, "2": 0, "3": 0, "4": 0, "5": 0 };
  const maxBar = Math.max(1, ...[5, 4, 3, 2, 1].map((r) => dist[String(r)] || 0));

  return (
    <div className="bg-white rounded-2xl border border-[#E2E8F0] shadow-lg p-6 md:p-8">
      <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6 md:gap-8 items-center">
        <div className="flex flex-col items-center text-center" data-testid="reviews-stats-summary">
          <div className="text-5xl md:text-6xl font-black text-[#1A1D3D] leading-none" dir="ltr">
            {avg.toFixed(1)}
          </div>
          <div className="mt-2"><StarsDisplay value={avg} size="w-5 h-5" /></div>
          <div className="text-sm text-[#4A5078] mt-2">
            {total > 0 ? `بناءً على ${total} تقييم` : "لا توجد تقييمات بعد"}
          </div>
        </div>

        <div className="space-y-2 min-w-0">
          {[5, 4, 3, 2, 1].map((r) => {
            const count = dist[String(r)] || 0;
            const pct = total > 0 ? (count / total) * 100 : 0;
            const barWidth = total > 0 ? (count / maxBar) * 100 : 0;
            return (
              <div key={r} className="flex items-center gap-3" data-testid={`rating-bar-${r}`}>
                <span className="text-xs font-bold text-[#1A1D3D] w-3 text-right" dir="ltr">{r}</span>
                <Star className="w-3.5 h-3.5 text-[#FBBF24] flex-shrink-0" fill="currentColor" />
                <div className="flex-1 h-2.5 bg-[#F1F5F9] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-[#FBBF24] to-[#F59E0B] rounded-full transition-all duration-700"
                    style={{ width: `${barWidth}%` }}
                  />
                </div>
                <span className="text-xs text-[#8B92B3] w-12 text-left" dir="ltr">
                  {count} ({pct.toFixed(0)}%)
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const PREVIEW_LIMIT = 6;
const DIALOG_PAGE_SIZE = 12;

const ReviewsSection = ({ user }) => {
  const [stats, setStats] = useState({ total: 0, average: 0, distribution: { "1": 0, "2": 0, "3": 0, "4": 0, "5": 0 } });
  const [reviews, setReviews] = useState([]); // preview list (PREVIEW_LIMIT)
  const [myReview, setMyReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);

  // "All reviews" dialog state
  const [allOpen, setAllOpen] = useState(false);
  const [allReviews, setAllReviews] = useState([]);
  const [allLoading, setAllLoading] = useState(false);
  const [allHasMore, setAllHasMore] = useState(true);
  const [allSort, setAllSort] = useState("newest"); // newest | highest | lowest

  const refresh = async () => {
    setLoading(true);
    try {
      const [statsRes, listRes] = await Promise.all([
        axios.get(`${API}/reviews/stats`),
        axios.get(`${API}/reviews?limit=${PREVIEW_LIMIT}&sort=newest`),
      ]);
      setStats(statsRes.data || stats);
      setReviews(Array.isArray(listRes.data) ? listRes.data : []);
      if (user) {
        try {
          const mine = await axios.get(`${API}/reviews/me`, { withCredentials: true });
          setMyReview(mine.data?.review || null);
        } catch (e) {
          setMyReview(null);
        }
      } else {
        setMyReview(null);
      }
    } catch (e) {
      // best-effort
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, [user?.user_id]);

  const canReview = !!(user && (user.enrolled || user.role === "admin"));

  // Sort preview: keep current user's review first
  const previewReviews = useMemo(() => {
    if (!user || !myReview) return reviews;
    const own = reviews.find((r) => r.review_id === myReview.review_id);
    if (!own) return reviews;
    return [own, ...reviews.filter((r) => r.review_id !== own.review_id)];
  }, [reviews, myReview, user]);

  // ----- Dialog (all reviews) -----
  const loadAllReviews = async (sort, reset = false) => {
    setAllLoading(true);
    try {
      const offset = reset ? 0 : allReviews.length;
      const res = await axios.get(`${API}/reviews?limit=${DIALOG_PAGE_SIZE}&offset=${offset}&sort=${sort}`);
      const incoming = Array.isArray(res.data) ? res.data : [];
      let next;
      if (reset) {
        next = incoming;
      } else {
        next = [...allReviews, ...incoming];
      }
      // Client-side sort for "highest" / "lowest" — keep stable on same fetch
      if (sort === "highest") {
        next = [...next].sort((a, b) => b.rating - a.rating || (b.created_at > a.created_at ? 1 : -1));
      } else if (sort === "lowest") {
        next = [...next].sort((a, b) => a.rating - b.rating || (b.created_at > a.created_at ? 1 : -1));
      }
      setAllReviews(next);
      setAllHasMore(incoming.length === DIALOG_PAGE_SIZE);
    } catch (e) {
      toast.error("تعذّر تحميل التقييمات");
    } finally {
      setAllLoading(false);
    }
  };

  const openAllDialog = () => {
    setAllOpen(true);
    setAllReviews([]);
    setAllHasMore(true);
    loadAllReviews(allSort, true);
  };

  const changeSort = (s) => {
    setAllSort(s);
    setAllReviews([]);
    setAllHasMore(true);
    loadAllReviews(s, true);
  };

  const totalCount = stats?.total || 0;
  const hasMoreThanPreview = totalCount > previewReviews.length;

  return (
    <section
      id="reviews"
      className="py-16 md:py-24 px-4 md:px-12 lg:px-24 bg-gradient-to-br from-[#FAF8FF] via-white to-[#FFF7ED]"
      style={{ fontFamily: 'Cairo, sans-serif' }}
      data-testid="reviews-section"
    >
      <div className="max-w-6xl mx-auto">
        {/* Section header */}
        <div className="text-center mb-10 md:mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FEF3C7] border border-[#FBBF24]/40 mb-4">
            <Sparkles className="w-3.5 h-3.5 text-[#D97706]" />
            <span className="text-xs font-bold text-[#92400E]">آراء المشاركين</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-[#1A1D3D] mb-3">
            ماذا قال المشاركون عن الكورس؟
          </h2>
          <p className="text-sm md:text-base text-[#4A5078] max-w-2xl mx-auto">
            تقييمات وتجارب حقيقية من مشاركين أكملوا الكورس.
          </p>
        </div>

        {/* Stats */}
        <div className="mb-8">
          <RatingSummary stats={stats} />
        </div>

        {/* Write/Edit Review CTA */}
        <div className="mb-10">
          {canReview ? (
            !formOpen ? (
              <div className="flex justify-center">
                <Button
                  onClick={() => setFormOpen(true)}
                  className="bg-gradient-to-r from-[#7C3AED] to-[#EC4899] hover:from-[#6D28D9] hover:to-[#DB2777] text-white px-6 py-6 text-base rounded-xl shadow-lg shadow-purple-500/25 hover:scale-[1.03] transition-all"
                  data-testid="open-review-form-button"
                >
                  {myReview ? <Pencil className="w-4 h-4 ml-2" /> : <Star className="w-4 h-4 ml-2" fill="currentColor" />}
                  {myReview ? "تعديل تقييمك" : "اكتب تقييمك"}
                </Button>
              </div>
            ) : (
              <ReviewForm
                existing={myReview}
                onSubmitted={() => { setFormOpen(false); refresh(); }}
                onCancel={() => setFormOpen(false)}
              />
            )
          ) : (
            user && (
              <div className="text-center text-sm text-[#8B92B3] py-2">
                التقييم متاح فقط للمشاركين المسجَّلين في الكورس.
              </div>
            )
          )}
        </div>

        {/* Reviews grid (preview only — capped) */}
        {loading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="w-8 h-8 text-[#7C3AED] animate-spin" />
          </div>
        ) : previewReviews.length === 0 ? (
          <div className="text-center py-12 text-[#8B92B3]">
            <Quote className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p>كن أول من يُشارك تجربته بعد الانضمام للكورس.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6" data-testid="reviews-grid">
              {previewReviews.slice(0, PREVIEW_LIMIT).map((r) => (
                <ReviewCard key={r.review_id} review={r} />
              ))}
            </div>

            {/* "View all" CTA — keeps the page compact when reviews grow */}
            {hasMoreThanPreview && (
              <div className="mt-8 flex justify-center">
                <Button
                  variant="outline"
                  onClick={openAllDialog}
                  className="px-6 py-5 text-sm md:text-base font-bold border-2 border-[#7C3AED]/30 text-[#7C3AED] hover:bg-[#7C3AED] hover:text-white hover:border-[#7C3AED] rounded-xl transition-all"
                  data-testid="view-all-reviews-button"
                >
                  <ChevronDown className="w-4 h-4 ml-2" />
                  عرض جميع التقييمات ({totalCount})
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* All-reviews Dialog */}
      <Dialog open={allOpen} onOpenChange={setAllOpen}>
        <DialogContent
          className="max-w-3xl max-h-[88vh] flex flex-col p-0 overflow-hidden"
          dir="rtl"
          style={{ fontFamily: 'Cairo, sans-serif' }}
          data-testid="all-reviews-dialog"
        >
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-[#E2E8F0]">
            <DialogTitle className="text-xl font-black text-[#1A1D3D] flex items-center gap-2">
              <Star className="w-5 h-5 text-[#FBBF24]" fill="currentColor" />
              جميع التقييمات ({totalCount})
            </DialogTitle>
            <DialogDescription className="text-sm text-[#4A5078]">
              متوسط: <span className="font-bold text-[#1A1D3D]" dir="ltr">{(stats?.average || 0).toFixed(1)}</span> / 5
            </DialogDescription>

            {/* Sort tabs */}
            <div className="flex items-center gap-2 mt-3">
              {[
                { id: "newest", label: "الأحدث" },
                { id: "highest", label: "الأعلى تقييمًا" },
                { id: "lowest", label: "الأدنى تقييمًا" },
              ].map((s) => (
                <button
                  key={s.id}
                  onClick={() => changeSort(s.id)}
                  className={`px-3 py-1.5 rounded-lg text-xs md:text-sm font-bold transition ${
                    allSort === s.id
                      ? "bg-[#7C3AED] text-white"
                      : "bg-[#F1F5F9] text-[#4A5078] hover:bg-[#E2E8F0]"
                  }`}
                  data-testid={`reviews-sort-${s.id}`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-6 py-5">
            {allLoading && allReviews.length === 0 ? (
              <div className="flex justify-center py-10">
                <Loader2 className="w-8 h-8 text-[#7C3AED] animate-spin" />
              </div>
            ) : allReviews.length === 0 ? (
              <div className="text-center py-10 text-[#8B92B3]">لا توجد تقييمات.</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {allReviews.map((r) => (
                  <ReviewCard key={r.review_id} review={r} />
                ))}
              </div>
            )}

            {allHasMore && allReviews.length > 0 && (
              <div className="mt-6 flex justify-center">
                <Button
                  variant="outline"
                  onClick={() => loadAllReviews(allSort, false)}
                  disabled={allLoading}
                  className="px-5"
                  data-testid="load-more-reviews-button"
                >
                  {allLoading ? <Loader2 className="w-4 h-4 ml-2 animate-spin" /> : <ChevronDown className="w-4 h-4 ml-2" />}
                  تحميل المزيد
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default ReviewsSection;
